import axios from 'axios';
import { parseJsonPreservingUnsafeIntegers, toJson } from 'json-with-bigint';
import { LEGO_CONSTANTS } from 'constants/index';
import { attachTypeBasedEntityApiHeaders } from './typeBasedEntityRequestHeaders';

axios.defaults.xsrfCookieName = undefined;
axios.defaults.xsrfHeaderName = undefined;
class AxiosClient {
  constructor() {
    let sessionCSRFToken = null;
    this.client = axios.create({
      timeout: 10000,
      withCredentials: false,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': '*',
        'Access-Control-Allow-Methods': 'GET,PUT,POST,DELETE',
      },
    });

    this.client.interceptors.request.use(
      request => {
        if (sessionCSRFToken) {
          request.headers[LEGO_CONSTANTS.X_XSRF_TOKEN.toUpperCase()] = sessionCSRFToken;
        }
        return attachTypeBasedEntityApiHeaders(request);
      },
      error => Promise.reject(error)
    );

    /**
     * Preserve integers beyond `Number.MAX_SAFE_INTEGER` in parsed response data (JSON.parse() alone cannot).
     */
    this.client.defaults.transformResponse = [
      function (entireResponseJsonAsString) {
        if (typeof entireResponseJsonAsString !== 'string') {
          return entireResponseJsonAsString;
        }
        try {
          return parseJsonPreservingUnsafeIntegers(entireResponseJsonAsString);
        } catch (error) {
          if (error instanceof SyntaxError) {
            console.error('Failed to parse response JSON:', error);
            return entireResponseJsonAsString;
          }
          throw error;
        }
      },
    ];

    this.client.interceptors.response.use(
      response => {
        sessionCSRFToken = response.headers[LEGO_CONSTANTS.X_XSRF_TOKEN];
        return response.data;
      },
      error => Promise.reject(error.response)
    );
  }

  /**
   * Serialize request bodies. Mirrors response parsing: `BigInt` becomes JSON *number* tokens
   * (including integers beyond `Number.MAX_SAFE_INTEGER`), not quoted strings.
   * `JSON.stringify` cannot emit BigInt; we feed string values with `BIGINT_SENTINEL_PREFIX` + digits,
   * then unwrap that quoted pattern to a JSON number.
   */
  toJson(data) {
    return toJson(data);
  }

  sendRequest(requestConfig) {
    const { url: requestUrl, method: requestMethod, body, headers: formDataHeaders } = requestConfig;
    const url = requestUrl ?? '';
    const method = requestMethod ?? 'GET';
    const formDataRequestHeaders = formDataHeaders?.headers;
    const data = formDataRequestHeaders?.[LEGO_CONSTANTS.CONTENT_TYPE] === LEGO_CONSTANTS.FORMDATA_MULTIPART ? body : this.toJson(body ?? {});
    return this.client.request({
      method,
      url,
      data,
      headers: { ...formDataRequestHeaders },
    });
  }
}

export const axiosClient = new AxiosClient();
